import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  id: "paymentDetails",
  class: "max-w-176 mt-12 shadow-md",
  "data-test-id": "payment_information_component"
}
const _hoisted_2 = {
  key: 0,
  class: "relative"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = {
  key: 1,
  class: "flex w-full px-6 py-5 rounded gap-4 bg-racq-info-light-blue mt-4"
}
const _hoisted_10 = { class: "flex flex-col w-full" }
const _hoisted_11 = { class: "text-racq-dark-grey text-base-bold" }
const _hoisted_12 = { class: "text-racq-dark-grey text-m mt-[2px]" }

import { onMounted, ref, computed, watch, nextTick } from 'vue';
import { InsurancePolicy, ISitecoreFormGenericInput } from '@/interfaces';
import { getFormInput } from '@/utils/form-input-helper';
import { SelfServiceService } from '@/services/store/self-service-service';
import ReviewSummaryItem from '@/components/ReviewSummary/ReviewSummaryItem.vue';
import SimpleReviewSummary from '@/components/ReviewSummary/SimpleReviewSummary.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { format as formatDate } from 'date-fns';
import { formatCurrency } from '@/utils/text-transforms';
import { Discriminators } from '@/constants/insurance-policy-const';
import { Field, Link } from '@sitecore-jss/sitecore-jss-vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { IPaymentInstrument, PaymentMethod } from '@/interfaces/self-service/defer-payment.interface';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { useRouter } from 'vue-router';
import AutoRenewSettingsModal from './AutoRenewSettingsModal.vue';
import { useAccountStore } from '@/store';
import { CardTypes } from '@/enums/payment-types.enum';
import { toMaskedCardNumber } from '@/utils/convert';

enum FormInputs {
  paymentInformation = 'paymentInformation',
  paymentTypeInfoBox = 'paymentTypeInfoBox',
  autoRenewInfoBox = 'autoRenewInfoBox',
  annualPaymentDueLabel = 'annualPaymentDueLabel',
  monthlyPaymentDueLabel = 'monthlyPaymentDueLabel',
  autoRenewModalDialog = 'autoRenewModalDialog',
}
enum Bookmarks {
  nextPaymentDueDate = 'nextPaymentDueDate',
  paymentType = 'paymentType',
  paymentDetails = 'paymentDetails',
  deferPaymentSessionId = 'deferPaymentSessionId',
  changePaymentDetailsSessionId = 'changePaymentDetailsSessionId',
  autoRenewSettings = 'autoRenewSettings',
}

enum SummaryItemNames {
  paymentDetails = 'PaymentDetails',
  autoRenewSettings = 'AutoRenewSettings',
  nextPaymentDueDate = 'NextPaymentDueDate',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductDetailsPaymentInformation',
  props: {
    content: {},
    id: {},
    currentPolicyTemp: {},
    selfServiceToggles: {},
    actionBarRuleResult: {}
  },
  setup(__props: any) {

const router = useRouter();

const props = __props;

const accountStore = useAccountStore();

const currentPolicy = ref<InsurancePolicy>();
const paymentInformations = ref<any>();

const paymentTypeInfoBox = ref<any>(getFormInput(props.content, FormInputs.paymentTypeInfoBox));
const autoRenewInfoBox = ref<any>(getFormInput(props.content, FormInputs.autoRenewInfoBox));
const autoRenewSettingsModalEl = ref<InstanceType<typeof AutoRenewSettingsModal>>();
const autoRenewSettingsModalDs = ref<any>();

const isCurrentTermResponsivePayment = computed(() => {
  // SSINSP.FR10.2
  return currentPolicy.value?.currentTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails;
});

const isPaymentDueForCurrentTerm = computed(() => {
  const currentTermPaymentDetails = currentPolicy.value?.currentTermPaymentDetails;
  return currentTermPaymentDetails?.isPaymentDue;
});

const isPaymentDueForRenewalTerm = computed(() => {
  const renewalTermPaymentDetails = currentPolicy.value?.renewalTermPaymentDetails;
  return renewalTermPaymentDetails?.isPaymentDue;
});

const getPaymentInfoPaymentDueInfo = computed(() => {
  if (!currentPolicy.value || (!isPaymentDueForCurrentTerm.value && !isPaymentDueForRenewalTerm.value)) {
    return '';
  }

  // Payment type should always be determined from the currentTerm
  if (isCurrentTermResponsivePayment.value) {
    // Handle responsive payments (non-direct debit)
    return getFormInput(props.content, FormInputs.annualPaymentDueLabel)?.label?.value ?? '';
  } else {
    // Handle direct debit payments
    const isAnnual = currentPolicy.value?.currentTermPaymentDetails?.isAnnual ?? false;
    return (
      getFormInput(props.content, isAnnual ? FormInputs.annualPaymentDueLabel : FormInputs.monthlyPaymentDueLabel)
        ?.label?.value ?? ''
    );
  }
});

const currentPolicyPaymentMethods = ref<IPaymentInstrument | null>(null);

onMounted(async () => {
  currentPolicy.value = props.currentPolicyTemp; // hand off the prop to the reactive currentPolicy
});

watch(currentPolicy, async () => {
  // Only populate the paymentInformation reviewSumary when there's a value in currentPolicy
  if (currentPolicy.value) {
    paymentInformations.value = [
      await getPaymentInformationSummaryModel(0),
      await getPaymentInformationSummaryModel(1),
    ];
  }

  if (!isCurrentTermResponsivePayment.value) {
    // Direct debit
    if (currentPolicyPaymentMethods.value === null) {
      let currentTerm = 0;
      if (currentPolicy.value?.state.isInRenewal) {
        currentTerm = currentPolicy.value!.renewalTermPaymentDetails?.termNumber;
      } else {
        currentTerm = currentPolicy.value!.currentTermPaymentDetails?.termNumber;
      }

      currentPolicyPaymentMethods.value = await SelfServiceService.getPaymentMethods(
        currentPolicy.value!.policyNumber!,
        currentTerm
      );
    }
  }
});

async function updateQueryStringForPrimaryLink(
  policyNumber: string,
  quoteSessionPage: SelfServiceSessionPages,
  currentQueryString: string | undefined,
  bookmark: string
): Promise<string | null> {
  if (currentQueryString === undefined) {
    return null;
  }
  const response = await SelfServiceService.createQuoteSession(policyNumber, quoteSessionPage);
  if (response?.success) {
    const updatedQueryString = replaceBookmark(bookmark, currentQueryString, response.data);
    return updatedQueryString;
  }
  return null;
}

async function getPaymentInformationSummaryModel(index: number): Promise<any> {
  const tempPaymentInformationSummary = getFormInput(props.content, FormInputs.paymentInformation);
  if (!tempPaymentInformationSummary) return { title: '', icon: '', items: [] };

  let items = tempPaymentInformationSummary.fields[index].items;

  if (isCurrentTermResponsivePayment.value) {
    // Remove direct debit only reviewSummaryItems and primary links
    const valuesToRemove = [SummaryItemNames.paymentDetails, SummaryItemNames.autoRenewSettings];
    items = items.filter((x: any) => !valuesToRemove.includes(x.name));
  }

  // Handle 'Defer payment' link
  const deferPaymentItemIndex = items.findIndex((x: any) => x.name === SummaryItemNames.nextPaymentDueDate);
  // Always looking at current term for defer payment link visibility
  const showDeferPaymentLink =
    !isCurrentTermResponsivePayment.value &&
    isPaymentDueForCurrentTerm.value &&
    !currentPolicy.value!.currentTermPaymentDetails.isAnnual;

  if (deferPaymentItemIndex !== -1) {
    if (!showDeferPaymentLink) {
      // Need to hide the 'Defer payment' link, so set it to undefined
      items[deferPaymentItemIndex].fields.primaryLink.value = undefined;
    } else {
      // Update 'Defer payment' primary link with a quoteSessionId
      const updatedPrimaryLinkQueryString = await updateQueryStringForPrimaryLink(
        currentPolicy.value!.policyNumber!,
        SelfServiceSessionPages.DeferPaymentPage,
        items[deferPaymentItemIndex].fields.primaryLink.value.querystring,
        Bookmarks.deferPaymentSessionId
      );

      items[deferPaymentItemIndex].fields.primaryLink.value.querystring = updatedPrimaryLinkQueryString;
    }
  }

  // Handle 'Change payment details' link
  const changePaymentDetailsItemIndex = items.findIndex((x: any) => x.name === SummaryItemNames.paymentDetails);
  const showChangePaymentDetailsLink =
    !isCurrentTermResponsivePayment.value && props.selfServiceToggles.isChangePaymentDetailsButtonEnabled;

  if (changePaymentDetailsItemIndex !== -1) {
    if (!showChangePaymentDetailsLink) {
      // Need to hide the 'Change payment details' link, so set it to undefined
      items[changePaymentDetailsItemIndex].fields.primaryLink.value = undefined;
    } else {
      // Update 'Change payment details' primary link with a quoteSessionId
      const updatedPrimaryLinkQueryString = await updateQueryStringForPrimaryLink(
        currentPolicy.value!.policyNumber!,
        SelfServiceSessionPages.ChangePaymentDetailsPage,
        items[changePaymentDetailsItemIndex].fields.primaryLink.value.querystring,
        Bookmarks.changePaymentDetailsSessionId
      );

      items[changePaymentDetailsItemIndex].fields.primaryLink.value.querystring = updatedPrimaryLinkQueryString;
    }
  }

  return {
    title: tempPaymentInformationSummary.fields[index].summaryTitle,
    icon: tempPaymentInformationSummary.fields[index].icon,
    items: items,
  };
}

async function switchToMonthly() {
  const response = await SelfServiceService.createQuoteSession(
    currentPolicy.value!.policyNumber,
    SelfServiceSessionPages.ChangeMonthlyPaymentPage
  );
  if (response?.success) {
    const newPath = '/My-RACQ/Insurance/Switch-to-monthly';
    const newQuery = { quoteSessionId: response.data };
    router.push({
      path: newPath,
      query: newQuery,
    });
  }
}

async function showAutoRenewSettingsModal() {
  const autoRenewModalDialogDs = getFormInput(props.content, FormInputs.autoRenewModalDialog) ?? {};
  const isAutoRenewEnabled = currentPolicy.value?.currentRenewalPreference ?? false;

  autoRenewSettingsModalDs.value = {
    ...autoRenewModalDialogDs,
    currentPolicy: currentPolicy.value,
    currentRenewalPreference: isAutoRenewEnabled ?? false,
  };
  await nextTick();
  autoRenewSettingsModalEl?.value.showModal();
}

async function handleAutoRenewUpdate(newState: boolean) {
  currentPolicy.value = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
    (x) => x.policyNumber === props.currentPolicyTemp?.policyNumber
  );

  //Force refresh of review summary
  paymentInformations.value = [await getPaymentInformationSummaryModel(0), await getPaymentInformationSummaryModel(1)];
  await nextTick();
}

function getPaymentInfoAutoRenewSettings(content: Field<string>): string {
  if (
    currentPolicy.value?.currentRenewalPreference === null ||
    currentPolicy.value?.currentRenewalPreference === undefined
  ) {
    return '';
  }
  const isAutoRenewEnabled = currentPolicy.value?.currentRenewalPreference ?? false;
  const iconClass = isAutoRenewEnabled ? 'bg-icon-success' : 'bg-icon-danger-dark-grey';
  const statusText = isAutoRenewEnabled ? 'Auto renew is on' : 'Auto renew is off';
  const icon = `<span class="${iconClass} icon w-4 h-4 bg-no-repeat bg-contain inline-block mr-2"></span>`;

  return replaceBookmark(Bookmarks.autoRenewSettings, content.value, `${icon}${statusText}`.replaceAll('"', "'"));
}

function getPaymentInfoPaymentDetails(content: Field<string>): string {
  const bankIcon = `<span class="bg-icon-bank-account icon w-3 h-3 bg-no-repeat bg-contain inline-block"></span>`;
  const mastercardIcon = `<span class="bg-icon-mastercard icon w-6 h-5 bg-no-repeat bg-contain inline-block align-middle"></span>`;
  const visaIcon = `<span class="bg-icon-visa icon w-3 h-3 bg-no-repeat bg-contain inline-block align-middle"></span>`;

  let paymentDetailsText = '';

  if (currentPolicyPaymentMethods.value?.paymentMethod === PaymentMethod.BankAccount) {
    // Bank direct debit
    paymentDetailsText = `${bankIcon}Bank account ${currentPolicyPaymentMethods.value.bankAccountDetails.bankStateBranchNumber} - ${currentPolicyPaymentMethods.value.bankAccountDetails.accountNumber}`;
  } else {
    // Card direct debit
    const cardType = currentPolicyPaymentMethods.value?.creditCardDetails.cardType;
    let cardIcon = '';
    if (cardType?.toLowerCase() === CardTypes.MasterCard) {
      cardIcon = mastercardIcon;
    } else if (cardType?.toLowerCase() === CardTypes.Visa) {
      cardIcon = visaIcon;
    } else {
      // Edge case
      cardIcon = bankIcon;
    }
    paymentDetailsText = `${cardIcon}${cardType} ${toMaskedCardNumber(currentPolicyPaymentMethods.value?.creditCardDetails.maskedCardNumber)} - ${currentPolicyPaymentMethods.value?.creditCardDetails.cardExpiry}`;
  }

  return replaceBookmark(Bookmarks.paymentDetails, content.value, paymentDetailsText.replaceAll('"', "'"));
}

const PAYMENT_TYPES = {
  ANNUAL_INVOICE: 'Annual invoice',
  MONTHLY_DIRECT_DEBIT: 'Monthly direct debit',
  ANNUAL_DIRECT_DEBIT: 'Annual direct debit',
} as const;

function getPaymentInfoPaymentType(content: Field<string>): string {
  if (!content?.value || !currentPolicy.value) {
    return '';
  }

  try {
    let paymentType: string;
    let isAnnual: boolean | undefined;

    // Payment type should always be determined from the currentTerm
    if (isCurrentTermResponsivePayment.value) {
      // non direct-debit
      paymentType = PAYMENT_TYPES.ANNUAL_INVOICE;
    } else {
      // direct debit
      isAnnual = currentPolicy.value.currentTermPaymentDetails.isAnnual;
      paymentType = isAnnual ? PAYMENT_TYPES.ANNUAL_DIRECT_DEBIT : PAYMENT_TYPES.MONTHLY_DIRECT_DEBIT;
    }

    return replaceBookmark(Bookmarks.paymentType, content.value, paymentType).replaceAll('"', "'");
  } catch (error) {
    console.error('Error in replaceBookmark:', error);
    return '';
  }
}

// Formats the duedate text for the Next Payment Due item
function formatDueDateText(dueDate: Date | undefined, amount: number | undefined, calendarIcon: string) {
  if (dueDate === undefined || amount === undefined) {
    return `${calendarIcon}No payments scheduled`;
  }
  return `${calendarIcon}${formatDate(dueDate, 'dd MMMM yyyy')} for ${formatCurrency(amount, 2)}*`;
}

function getPaymentInfoNextDueDate(content: Field<string>): string {
  const calendarIcon = `<span class="bg-calendar-filled-icon icon w-3 h-3 bg-no-repeat bg-contain inline-block mr-2"></span>`;
  let nextDueDateText = `${calendarIcon}No payments scheduled`;

  if (!currentPolicy.value) {
    return replaceBookmark(Bookmarks.nextPaymentDueDate, content.value, nextDueDateText.replaceAll('"', "'"));
  }

  const { currentTermPaymentDetails, renewalTermPaymentDetails } = currentPolicy.value;

  if (isPaymentDueForCurrentTerm.value && currentTermPaymentDetails) {
    // Current term payment due
    nextDueDateText = formatDueDateText(
      currentTermPaymentDetails.nextInvoiceDueDate,
      currentTermPaymentDetails.nextInvoiceAmountDue,
      calendarIcon
    );
  } else if (isPaymentDueForRenewalTerm.value && renewalTermPaymentDetails) {
    // Renewal term payment due
    nextDueDateText = formatDueDateText(
      renewalTermPaymentDetails.nextInvoiceDueDate,
      renewalTermPaymentDetails.nextInvoiceAmountDue,
      calendarIcon
    );
  }

  return replaceBookmark(Bookmarks.nextPaymentDueDate, content.value, nextDueDateText.replaceAll('"', "'"));
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paymentInformations.value, (summary, summaryIndex) => {
        return (_openBlock(), _createBlock(SimpleReviewSummary, {
          key: summaryIndex,
          title: summary.title,
          icon: summary.icon,
          multiple: true
        }, {
          reviewSummaryItems: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summary.items, (item, itemIndex) => {
              return (_openBlock(), _createBlock(ReviewSummaryItem, {
                key: itemIndex,
                item: item,
                isLastReviewSummaryItem: !(itemIndex < summary.items.length - 1),
                isSelfService: true
              }, {
                title: _withCtx(() => [
                  (item.fields.title.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", {
                          class: "text-racq-dark-grey text-base-bold",
                          innerHTML: item.fields.title.value
                        }, null, 8, _hoisted_3)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                content: _withCtx(() => [
                  (item.name === 'NextPaymentDueDate')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "text-base",
                        innerHTML: getPaymentInfoNextDueDate(item.fields.content)
                      }, null, 8, _hoisted_4))
                    : _createCommentVNode("", true),
                  (item.name === 'PaymentType')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: "text-base",
                        innerHTML: getPaymentInfoPaymentType(item.fields.content)
                      }, null, 8, _hoisted_5))
                    : _createCommentVNode("", true),
                  (item.name === 'PaymentDetails' && currentPolicyPaymentMethods.value !== null)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 2,
                        class: "text-base flex items-center gap-[6px]",
                        innerHTML: getPaymentInfoPaymentDetails(item.fields.content)
                      }, null, 8, _hoisted_6))
                    : _createCommentVNode("", true),
                  (item.name === 'AutoRenewSettings')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 3,
                        class: "text-base",
                        innerHTML: getPaymentInfoAutoRenewSettings(item.fields.content)
                      }, null, 8, _hoisted_7))
                    : _createCommentVNode("", true)
                ]),
                secondaryLinks: _withCtx(() => _cache[2] || (_cache[2] = [])),
                edit: _withCtx(() => [
                  (item.fields?.primaryLink?.value?.href)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (item.fields?.primaryLink)
                          ? (_openBlock(), _createBlock(_unref(Link), {
                              key: 0,
                              field: item.fields.primaryLink,
                              "data-test-id": `link_${item.fields.primaryLink.value.title?.replaceAll(' ', '')}`,
                              class: "link text-base-bold"
                            }, null, 8, ["field", "data-test-id"]))
                          : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true),
                  (item.name === 'AutoRenewSettings')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        (currentPolicy.value?.policyNumber)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: _normalizeClass(["cursor-pointer link text-base-bold", { disabled: currentPolicy.value?.state?.isInRenewal }]),
                              "data-test-id": "link_AutoRenewSettingModal",
                              onClick: _cache[0] || (_cache[0] = ($event: any) => (showAutoRenewSettingsModal()))
                            }, "Change settings", 2))
                          : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                subContent: _withCtx(() => [
                  (item.name === 'NextPaymentDueDate' && getPaymentInfoPaymentDueInfo.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "text-sm",
                        innerHTML: getPaymentInfoPaymentDueInfo.value
                      }, null, 8, _hoisted_8))
                    : _createCommentVNode("", true),
                  (item.name === 'PaymentType' && _ctx.actionBarRuleResult.showSwitchToMonthly)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _cache[4] || (_cache[4] = _createElementVNode("div", null, [
                          _createElementVNode("span", { class: "w-5 h-6 flex-col justify-center items-start gap-2.5 inline-flex bg-icon-info" })
                        ], -1)),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("div", _hoisted_11, _toDisplayString(paymentTypeInfoBox.value?.title.value), 1),
                          _createElementVNode("div", _hoisted_12, [
                            _createTextVNode(_toDisplayString(paymentTypeInfoBox.value?.content.value) + " ", 1),
                            _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1)),
                            _createElementVNode("a", {
                              class: "cursor-pointer link text-base-bold",
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (switchToMonthly()))
                            }, "Pay monthly")
                          ])
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  (
                item.name === 'AutoRenewSettings' && currentPolicy.value!.state.isInRenewal && !isCurrentTermResponsivePayment.value
              )
                    ? (_openBlock(), _createBlock(InformationBox, {
                        key: 2,
                        description: autoRenewInfoBox.value.content.value,
                        type: _unref(InformationBoxTypes).WARNING
                      }, null, 8, ["description", "type"]))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["item", "isLastReviewSummaryItem"]))
            }), 128))
          ]),
          _: 2
        }, 1032, ["title", "icon"]))
      }), 128))
    ]),
    _createVNode(AutoRenewSettingsModal, {
      ref_key: "autoRenewSettingsModalEl",
      ref: autoRenewSettingsModalEl,
      autoRenewSettingsModal: autoRenewSettingsModalDs.value,
      onUpdateAutoRenewState: handleAutoRenewUpdate
    }, null, 8, ["autoRenewSettingsModal"])
  ], 64))
}
}

})